/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Thu Oct 19 2023
 **/

import React, { useCallback, useEffect, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import debounce from "lodash-es/debounce";

import { getChatTitleByUserList, useChatMessageActions, useDMRoomState } from "@/frameworks/core/chat";
import { useFullSizeChatManager, useMinimizeChatList, usePopover, useRoomViewActions } from "@/frameworks/core/hooks";
import { Button, Column, Divider, Row, Typography } from "@/frameworks/designStuff/components";
import { EventID, GROUP_POPOVER_ID, ZIndex } from "@/frameworks/core/commons";
import { Configure } from "@/frameworks/libraries/utilities";
import Language, { _T } from "@rfice/language";
import { AvatarGroup } from "@/frameworks/designStuff/compounds";
import { Properties } from "@/frameworks/libraries/properties";
import { Maybe } from "@/frameworks/libraries/components";
import { Avatar } from "@/frameworks/core/components";
import { useChatDataStore } from "@/modules/chat/store";
import { useTheme } from "@emotion/react";

import { VoiceCallLayer, MessageInput, MessageList } from "../../../../../Common";
import { DMChatOptionList } from "../../../../DMChatOptionList";
import { EventHandler, useEventHandler } from "@rfice/eventhandler";
import { MessageSearchInConversation, MessageSearchResult } from "../../../MiniChatPopup/components";
import { SearchedResultWrapper } from "../../space/components/FullSizeSpaceChat/private/emotion";

const ELLIPSIS_OFFSET = 130;

export default function FullSizeDMChat({
	channelID,
	activityMessageID
}: {
	channelID: string;
	activityMessageID?: string;
}) {
	const { colors } = useTheme();
	const popover = usePopover();
	const { messageReaded } = useChatMessageActions();
	const { minimizeChatListActions } = useMinimizeChatList();
	const { fullSizeChatActions } = useFullSizeChatManager();
	const { fullSizeChannelID, setFullSizeChannelID } = useChatDataStore(
		useShallow((state) => ({
			fullSizeChannelID: state.fullsize_chat_id,
			setFullSizeChannelID: state.set_fullsize_chat_id
		}))
	);

	const { updateRoomView } = useRoomViewActions();

	const channelData = useDMRoomState(channelID);

	const [isShowMessageSearchResult, setIsShowMessageSearchResult] = useState(false);
	const [searchText, setSearchText] = useState<string>("");
	const [isSearchInputOpened, setIsSearchInputOpened] = useState(false);

	useEventHandler(
		EventID.TOGGLE_OPEN_SEARCH_ON_CONVERSATION_INPUT,
		({ roomId: targetRoomId }: { roomId: string }) => {
			if (targetRoomId !== channelID) return;
			setIsSearchInputOpened((prev) => !prev);
			setIsShowMessageSearchResult(Boolean(searchText && !isSearchInputOpened));
		},
		[channelID, searchText, isSearchInputOpened]
	);

	const toggleOpenSearchChatInput = useCallback(() => {
		EventHandler.emit(EventID.TOGGLE_OPEN_SEARCH_ON_CONVERSATION_INPUT, { roomId: channelID });
	}, [channelID]);

	useEventHandler(
		EventID.OPEN_SEARCH_RESULT_ON_MINI_CHAT,
		({
			searchValue,
			isOpenSearchResult,
			roomId: targetRoomId
		}: {
			searchValue: string;
			isOpenSearchResult: boolean;
			roomId: string;
		}) => {
			if (targetRoomId !== channelID) return;
			if (isOpenSearchResult) {
				setIsShowMessageSearchResult(Boolean(searchValue));
				if (!searchValue || searchValue.trim().length === 0) return;
				setSearchText(searchValue);
			} else {
				setSearchText("");
				setIsShowMessageSearchResult(false);
			}
		},
		[channelID]
	);

	const handleOnClearSearchInput = () => {
		setIsShowMessageSearchResult(false);
		setSearchText("");
	};

	const handleSearchCompleted = () => {
		setIsSearchInputOpened(false);
		setSearchText("");
		setIsShowMessageSearchResult(false);
	};

	const handleCloseSearchUI = () => {
		setIsSearchInputOpened(false);
		setIsShowMessageSearchResult(false);
	};

	useEffect(() => {
		if (fullSizeChannelID && channelID !== fullSizeChannelID) {
			updateRoomView({
				roomId: fullSizeChannelID as string,
				deviceKey: Properties.browserTabID,
				view: false
			});
		}

		setFullSizeChannelID(channelID);
	}, [channelID]);

	const handleShowRecentMessage = useCallback(
		debounce((message) => {
			if (!channelData || message.isMe) return;
			if (message.roomMessageId === channelData.messageLastReadId) return;
			messageReaded({ channelID: channelID, messageID: message.roomMessageId });
		}, 1000),
		[channelData?.messageLastReadId]
	);

	if (!channelData) return;

	const channelUserList = channelData.users ?? [];
	const userListForMention = channelUserList.map((user) => ({
		name: user.name,
		src: user.profileImagePath,
		userID: user.id
	}));

	const blockUsers = channelUserList.filter((userInfo) => userInfo.blocked);

	const usersToDisplay =
		channelData.users.length > 1
			? channelData.users.filter((user) => user.id !== Configure.userID)
			: channelData.users;

	const handleClickIcon = (ev: React.MouseEvent<HTMLButtonElement>) => {
		const target = ev.currentTarget as HTMLButtonElement;

		switch (target.id) {
			case "more": {
				popover.show({
					popoverID: GROUP_POPOVER_ID.CHAT_OPTION,
					uniqueID: "option",
					content: <DMChatOptionList channelID={channelID} />,
					anchorEl: target,
					direction: {
						x: "right",
						y: "bottom"
					},
					position: {
						x: Language.current() === "ja" ? -303 : Language.current() === "en" ? -240 : -165,
						y: 0
					},
					zIndex: ZIndex.POPUP
				});

				break;
			}
			case "close": {
				minimizeChatListActions.remove(channelID);
				fullSizeChatActions.hide();
				break;
			}
		}
	};

	return (
		<Column css={{ width: "100%", height: "100%" }}>
			<Row
				verticalAlign="center"
				horizonAlign="distribute"
				css={{ width: "100%", padding: "15px 20px", height: "56px" }}
			>
				<Row gap={12} verticalAlign="center" css={{ width: "100%" }}>
					<Button.Icon id="close" name="Left24" size="large" filled="transparent" onClick={handleClickIcon} />
					<AvatarGroup
						size="24px"
						avatarList={usersToDisplay.map((user) => {
							const { id } = user;
							return <Avatar id={id} kind="people_24" useStatus={false} key={id} />;
						})}
					/>
					<Row verticalAlign="center" css={{ maxWidth: `calc(100% - ${ELLIPSIS_OFFSET}px)` }}>
						<Typography typoSize="Head6">
							{getChatTitleByUserList(channelData.users)}{" "}
							{`${channelData.users.length === 1 ? _T("(나)") : ""}`}
						</Typography>
					</Row>
					<Maybe test={channelData.users.length > 2}>
						<Typography typoSize="Body4" typoColor="neutral_60_100">
							{_T("채팅멤버 {{count}}명", { count: channelData.users.length })}
						</Typography>
					</Maybe>
				</Row>
				<Row gap={12}>
					<Button.Icon name="Search24" size="large" variant="text" onClick={toggleOpenSearchChatInput} />
					<Button.Icon id="more" name="More24" size="large" filled="transparent" onClick={handleClickIcon} />
					<Button.Icon
						id="close"
						name="Close24"
						size="large"
						filled="transparent"
						onClick={handleClickIcon}
					/>
				</Row>
			</Row>
			<Divider type="line" />
			<Maybe test={isSearchInputOpened && channelID}>
				<Row
					verticalAlign="center"
					gap={8}
					style={{
						width: "100%",
						padding: "4px 60px 4px 60px",
						backgroundColor:
							isShowMessageSearchResult && Boolean(searchText.trim())
								? colors.accent_10_100
								: colors.neutral_10_100
					}}
				>
					<MessageSearchInConversation
						searchedText={searchText}
						onClear={handleOnClearSearchInput}
						roomId={channelID}
					/>
					<Row gap={8} verticalAlign="center" style={{ paddingRight: 8 }}>
						<Button.Icon
							shape="round"
							name="Close24"
							size="medium_18"
							variant="text"
							onClick={handleCloseSearchUI}
						/>
					</Row>
				</Row>
			</Maybe>

			<Maybe test={!isShowMessageSearchResult && !isSearchInputOpened}>
				<Row style={{ width: "100%", padding: "8px 60px" }} horizonAlign="center">
					<VoiceCallLayer chatSize="full" roomID={channelData.roomId} />
				</Row>
			</Maybe>

			{isShowMessageSearchResult && Boolean(searchText.trim()) ? (
				<SearchedResultWrapper>
					<MessageSearchResult
						onComplete={handleSearchCompleted}
						searchText={searchText}
						roomID={channelID}
					/>
				</SearchedResultWrapper>
			) : (
				<MessageList
					variant="fullSize"
					roomID={channelID}
					onShowRecentMessage={handleShowRecentMessage}
					blockedUsers={blockUsers?.map((user) => user.id)}
					notice={channelData.notice}
					voteNotice={channelData.voteNotice}
					myRoomID={channelData.myRoomId}
					isMyRoom={channelData.myRoom}
					activityMessageID={activityMessageID}
				/>
			)}

			<Maybe test={!isShowMessageSearchResult}>
				<MessageInput
					id={channelID}
					type="DM"
					userList={userListForMention}
					variant="fullSize"
					myRoomID={channelData.myRoomId}
					isMyRoom={channelData.myRoom}
				/>
			</Maybe>
		</Column>
	);
}
